import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  BANDS_DJS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordPlural } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import { useEffectOnce, useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks';
import H from '@zola/zola-ui/src/typography/Headings';

import { CardShelf } from '~/components/common/cards/CardShelf/CardShelf';
import { storefrontCardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import VendorCard from '~/components/common/cards/VendorCard/VendorCard';
import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { TrackingProductLocation } from '~/util/trackingHelper';

import { PageSection } from '../../../PageSection/PageSection';

import styles from './adjacentVendors.module.less';

export const AdjacentVendors = (): JSX.Element => {
  useForceUpdateOnWindowResize();

  const { adjacentVendors, vendorSearch } = useSearchResultsPageContext();
  const { isDesktop: isInitialDesktop } = useResponsiveDesign();
  const { selectedLocation } = vendorSearch;

  useEffect(() => {
    if (adjacentVendors.length > 0) {
      doTrackStorefrontListViewed({
        storefrontList: adjacentVendors,
        taxonomyKey: vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey | undefined,
        listingShelfName: 'ADJACENT',
        includeTags: true,
      });
    }
  }, [adjacentVendors, vendorSearch.selectedTaxonomy?.key]);

  const [showCarousel, setShowCarousel] = useState(isInitialDesktop);
  useEffectOnce(() => {
    setShowCarousel(isDesktop());
  });

  const hed = useMemo(() => {
    const { label } = selectedLocation || {};
    const vendorWordPlural = getVendorWordPlural(vendorSearch.selectedTaxonomy?.key);
    if (label) {
      return `Location flexible? Discover ${vendorWordPlural} near ${label}`;
    }
    return `Location flexible? Discover ${vendorWordPlural} nearby`;
  }, [selectedLocation, vendorSearch.selectedTaxonomy?.key]);

  if (adjacentVendors.length === 0) {
    return <Fragment />;
  }

  return (
    <PageSection>
      <div className={styles.adjacentVendors}>
        <H.Title2 presentation="h3">{hed}</H.Title2>
        <CardShelf>
          {adjacentVendors.map((vendor, index) => {
            const mappedVendor = storefrontCardViewToVendorCardVendor(vendor);

            return (
              <VendorCard
                key={vendor.storefrontUuid}
                vendor={mappedVendor}
                trackClick
                listingShelfName="ADJACENT"
                showDetails={false}
                showPrice={false}
                position={index + 1}
                location={TrackingProductLocation.SEARCH_RESULTS_ADJACENT_VENDORS_CAROUSEL}
                showCarousel={showCarousel}
                className={styles.adjacentVendorCard}
                showVendorType={vendorSearch.selectedTaxonomy?.key === BANDS_DJS_TAXONOMY_KEY}
              />
            );
          })}
        </CardShelf>
      </div>
    </PageSection>
  );
};
